/**
 * Kundenstimmen für die öffentliche Seite pflegen.
 *
 * Der Bereich ist bewusst schmal gehalten: eine Liste, ein Blatt zum
 * Bearbeiten, zwei Pfeile für die Reihenfolge. Alles Weitere wäre Aufwand
 * für etwas, das ein paar Mal im Jahr angefasst wird.
 *
 * Zwei Dinge sind hier keine Kleinigkeit:
 *
 *  - Eine neue Referenz steht **nicht** sofort im Netz. Sie ist ein Entwurf,
 *    bis jemand sie ausdrücklich veröffentlicht. Wer einen Namen nennt, soll
 *    das tun wollen und nicht bemerken.
 *  - Neben dem Schalter steht das Datum der Freigabe durch den Kunden. Es
 *    sperrt nichts – aber die Liste sagt deutlich, wenn eine veröffentlichte
 *    Stimme keines hat. Wer später gefragt wird, ob die Gemeinde zugestimmt
 *    hat, will die Antwort nicht suchen müssen.
 */

import { useState } from 'react'
import {
  useReferenzAendern,
  useReferenzAnlegen,
  useReferenzLoeschen,
  useReferenzen,
  type Referenz,
} from '../api/referenzen'
import { Blatt } from '../bausteine/Blatt'
import { ausDeutsch, nachDeutsch } from '../bausteine/Kalender'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'

export function Referenzen() {
  const liste = useReferenzen()
  const aendern = useReferenzAendern()
  const [bearbeitet, setzeBearbeitet] = useState<Referenz | 'neu' | null>(null)

  if (liste.isLoading) return <Ladeanzeige />
  if (liste.error) return <Fehlerhinweis fehler={liste.error} />

  const referenzen = liste.data ?? []

  /* Reihenfolge ändern, indem zwei Nachbarn ihre Sortierung tauschen.
     Zwei Anfragen statt einer eigenen Schnittstelle: bei einer Handvoll
     Einträgen ist das billiger als ein Weg, den es nur dafür gäbe. */
  function tauschen(i: number, j: number) {
    const a = referenzen[i]
    const b = referenzen[j]
    if (!a || !b) return
    aendern.mutate({ id: a.id, sortierung: b.sortierung })
    aendern.mutate({ id: b.id, sortierung: a.sortierung })
  }

  return (
    <div className="space-y-4">
      <p className="rounded-feld border-2 border-marke-100 bg-marke-50 p-3 text-marke-700">
        Diese Stimmen stehen auf der öffentlichen Terminseite unter dem Formular.
        Eine neue Referenz ist zunächst ein Entwurf – erst der Schalter
        „Veröffentlicht" stellt sie ins Netz.
      </p>

      <button
        type="button"
        className="knopf-haupt w-full"
        onClick={() => setzeBearbeitet('neu')}
      >
        Referenz hinzufügen
      </button>

      <Fehlerhinweis fehler={aendern.error} />

      {referenzen.length === 0 ? (
        <Leerhinweis
          titel="Noch keine Referenzen"
          text="Auf der öffentlichen Seite erscheint der Abschnitt erst, wenn hier eine veröffentlichte Stimme steht."
        />
      ) : (
        <ul className="space-y-2">
          {referenzen.map((referenz, i) => (
            <li key={referenz.id} className="karte">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{referenz.einrichtung}</span>
                    {referenz.ort && (
                      <span className="text-slate-600">{referenz.ort}</span>
                    )}
                    {referenz.aktiv ? (
                      <span className="marke bg-stufe-ob-grund text-stufe-ob">
                        Auf der Seite
                      </span>
                    ) : (
                      <span className="marke bg-slate-100 text-slate-700">Entwurf</span>
                    )}
                    {referenz.aktiv && !referenz.freigegeben_am && (
                      <span className="marke bg-stufe-2-grund text-stufe-2">
                        Freigabe fehlt
                      </span>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 text-base text-slate-700">
                    {referenz.text}
                  </p>
                </div>

                <div className="flex shrink-0 flex-col gap-1">
                  <button
                    type="button"
                    className="knopf-still h-10 w-12 px-0"
                    disabled={i === 0}
                    aria-label={`${referenz.einrichtung} nach oben`}
                    onClick={() => tauschen(i, i - 1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="knopf-still h-10 w-12 px-0"
                    disabled={i === referenzen.length - 1}
                    aria-label={`${referenz.einrichtung} nach unten`}
                    onClick={() => tauschen(i, i + 1)}
                  >
                    ↓
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="knopf-zweit mt-3 w-full"
                onClick={() => setzeBearbeitet(referenz)}
              >
                Bearbeiten
              </button>
            </li>
          ))}
        </ul>
      )}

      {bearbeitet && (
        <Referenzblatt
          referenz={bearbeitet === 'neu' ? null : bearbeitet}
          schliessen={() => setzeBearbeitet(null)}
        />
      )}
    </div>
  )
}

const LEER = {
  einrichtung: '',
  ort: '',
  text: '',
  person: '',
  funktion: '',
  aktiv: false,
}

function Referenzblatt({
  referenz,
  schliessen,
}: {
  referenz: Referenz | null
  schliessen: () => void
}) {
  const anlegen = useReferenzAnlegen()
  const aendern = useReferenzAendern()
  const loeschen = useReferenzLoeschen()

  const [stand, setzeStand] = useState(
    referenz
      ? {
          einrichtung: referenz.einrichtung,
          ort: referenz.ort ?? '',
          text: referenz.text,
          person: referenz.person ?? '',
          funktion: referenz.funktion ?? '',
          aktiv: referenz.aktiv,
        }
      : LEER,
  )
  // Das Freigabedatum wird deutsch getippt, nicht als `input type="date"` –
  // das stellt das Datum in der Sprache des Browsers dar. Siehe Datumwahl.
  const [freigabe, setzeFreigabe] = useState(
    referenz?.freigegeben_am ? nachDeutsch(referenz.freigegeben_am) : '',
  )
  const [sicherheitsfrage, setzeSicherheitsfrage] = useState(false)

  const freigabeIso = freigabe.trim() ? ausDeutsch(freigabe.trim()) : ''
  const datumFalsch = freigabe.trim() !== '' && freigabeIso === ''
  const vollstaendig = stand.einrichtung.trim().length >= 2 && stand.text.trim().length >= 10
  const laeuft = anlegen.isPending || aendern.isPending || loeschen.isPending

  function speichern() {
    const daten = {
      einrichtung: stand.einrichtung.trim(),
      ort: stand.ort.trim() || null,
      text: stand.text.trim(),
      person: stand.person.trim() || null,
      funktion: stand.funktion.trim() || null,
      aktiv: stand.aktiv,
      freigegeben_am: freigabeIso || null,
    }
    const auftrag = referenz
      ? aendern.mutateAsync({ id: referenz.id, ...daten })
      : anlegen.mutateAsync(daten)
    void auftrag.then(schliessen).catch(() => {})
  }

  return (
    <Blatt
      titel={referenz ? referenz.einrichtung : 'Neue Referenz'}
      untertitel={referenz ? 'Kundenstimme bearbeiten' : 'Erscheint zunächst als Entwurf'}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf-haupt flex-1"
            onClick={speichern}
            disabled={laeuft || !vollstaendig || datumFalsch}
          >
            {laeuft ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="r-einrichtung">Einrichtung *</label>
          <input
            id="r-einrichtung"
            value={stand.einrichtung}
            maxLength={150}
            onChange={(e) => setzeStand((v) => ({ ...v, einrichtung: e.target.value }))}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="r-ort">Ort</label>
            <input
              id="r-ort"
              value={stand.ort}
              maxLength={100}
              onChange={(e) => setzeStand((v) => ({ ...v, ort: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="r-person">Person</label>
            <input
              id="r-person"
              value={stand.person}
              maxLength={120}
              placeholder="M. Beispiel"
              onChange={(e) => setzeStand((v) => ({ ...v, person: e.target.value }))}
            />
          </div>
        </div>

        <div>
          <label htmlFor="r-funktion">Funktion</label>
          <input
            id="r-funktion"
            value={stand.funktion}
            maxLength={120}
            placeholder="Bauhofleitung"
            onChange={(e) => setzeStand((v) => ({ ...v, funktion: e.target.value }))}
          />
        </div>

        <div>
          <label htmlFor="r-text">Was gesagt wurde *</label>
          <textarea
            id="r-text"
            value={stand.text}
            maxLength={600}
            rows={5}
            onChange={(e) => setzeStand((v) => ({ ...v, text: e.target.value }))}
          />
          <p className="mt-1 text-base text-slate-600">
            {stand.text.trim().length} von 600 Zeichen. Kurz gehalten – im Karussell
            steht der Satz auf einer Karte.
          </p>
        </div>

        <div>
          <label htmlFor="r-freigabe">Freigabe des Kunden am</label>
          <input
            id="r-freigabe"
            value={freigabe}
            inputMode="numeric"
            placeholder="TT.MM.JJJJ"
            onChange={(e) => setzeFreigabe(e.target.value)}
          />
          {datumFalsch && (
            <p className="mt-1 text-base text-stufe-3">
              Kein gültiges Datum. Erwartet wird TT.MM.JJJJ.
            </p>
          )}
          <p className="mt-1 text-base text-slate-600">
            Wann die Einrichtung der Veröffentlichung zugestimmt hat. Das Feld hält
            nur fest – gefragt wird man trotzdem irgendwann.
          </p>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-6 w-6"
            checked={stand.aktiv}
            onChange={(e) => setzeStand((v) => ({ ...v, aktiv: e.target.checked }))}
          />
          <span>
            Veröffentlicht
            <span className="mt-0.5 block font-normal text-slate-600">
              Steht dann für jeden sichtbar auf der Terminseite.
            </span>
          </span>
        </label>

        {stand.aktiv && !freigabeIso && (
          <p className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3 text-stufe-2">
            Veröffentlicht, aber ohne Datum der Freigabe. Das ist zulässig – nur
            steht dann nirgends, wann die Einrichtung zugestimmt hat.
          </p>
        )}

        <Fehlerhinweis fehler={anlegen.error ?? aendern.error ?? loeschen.error} />

        {referenz && (
          <div className="border-t-2 border-slate-200 pt-3">
            {sicherheitsfrage ? (
              <div className="space-y-2">
                <p>Diese Referenz endgültig löschen?</p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="knopf-still flex-1"
                    onClick={() => setzeSicherheitsfrage(false)}
                  >
                    Behalten
                  </button>
                  <button
                    type="button"
                    className="knopf-warnung flex-1"
                    disabled={laeuft}
                    onClick={() =>
                      void loeschen.mutateAsync(referenz.id).then(schliessen).catch(() => {})
                    }
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="knopf-warnung w-full"
                onClick={() => setzeSicherheitsfrage(true)}
              >
                Referenz löschen
              </button>
            )}
          </div>
        )}
      </div>
    </Blatt>
  )
}
